'use client'

import { useEffect } from 'react'
import { toast, type ToasterProps } from 'sonner'

import { ToastActions } from './ToastActions'
import { cn } from '@lib/cn'
import { subscribeEmitToast, type BusProps } from '@lib/emitToast'

const LINK_TOAST_DURATION_MS = 5 * 60 * 1000

const progressBarColors: Record<string, string> = {
  success: 'bg-success',
  error: 'bg-destructive-foreground',
  warning: 'bg-warning',
  info: 'bg-primary',
}

function ToastProgressBar({ duration, type }: { duration: number; type: string }) {
  return (
    <div
      className={cn(
        'toast-progress-bar absolute bottom-0 left-0 right-0 h-[1px] origin-left rounded-b-lg animate-toast-progress opacity-60',
        progressBarColors[type] ?? 'bg-muted-foreground',
      )}
      style={{ animationDuration: `${duration}ms` }}
    />
  )
}

export function ToastErrorListener() {
  useEffect(() => {
    const unsubscribe = subscribeEmitToast((props: BusProps) => {
      if (typeof window === 'undefined') {
        console.error('ToastErrorListener - error:', props)

        return
      }

      const {
        message = 'Error',
        type = 'error',
        details = [],
        duration = 15000,
        closeButton = true,
        icon = null,
        action,
        actions,
      } = props
      const title = message
      const description = details?.map((d) => d.message).join('; \n')

      const Description = description ? <div className='whitespace-pre-line'>{`${description}.`}</div> : null

      const toastDuration = actions?.length ? LINK_TOAST_DURATION_MS : duration

      toast(title, {
        title,
        description: (
          <>
            {Description}
            <ToastActions actions={actions} />
            <ToastProgressBar duration={toastDuration} type={type ?? 'error'} />
          </>
        ),
        type,
        dismissible: true,
        duration: toastDuration,
        closeButton,
        icon,
        action,
      } as ToasterProps)
    })

    return unsubscribe
  }, [])

  return null
}
